import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, TextField, Button, Alert, CircularProgress, Link as MUILink, InputAdornment, IconButton } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import SessionCreate from '../../graphql/mutations/SessionCreate';
import Note from './Note';
import { useTranslation } from 'react-i18next';
import { LIBRARY, PROGRAM, accountFromSession, signIn } from '../../helpers/accounts';

const SignIn = ({ scope = LIBRARY }) => {
    const isProgram = scope === PROGRAM;
    const [isLoading, setIsLoading] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { t } = useTranslation();

    const handleSignIn = () => {
        try {
            setIsLoading(true);
            SessionCreate.commit({ email, password, scope: scope.toUpperCase() }).then(({ sessionCreate }) => {
                const { token, error } = sessionCreate || {};

                if (token) {
                    // Replaces whatever was signed in before: one account at a time.
                    signIn(scope, { ...accountFromSession(sessionCreate.user), token });
                    navigate('/catalogue'); // Redirect to home page
                    window.location.reload(true);
                } else {
                    setError(error || t('somethingWentWrong', 'Something went wrong'));
                }
                setIsLoading(false);
            })
        } catch (err) {
            setIsLoading(false);
            console.error('Sign-in error:', err);
            toast.error(t('unexpectedError', 'An unexpected error occurred.'));
        }
    };

    return (
        <Container maxWidth="lg">
            <Box
                sx={{
                    maxWidth: '400px',
                    margin: '50px auto',
                    padding: 4,
                    bgcolor: 'background.paper',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        color: 'primary.main',
                    }}
                >
                    {isProgram ? t('program.signIn', 'Reading programme') : t('signIn', 'Sign In')}
                </Typography>
                {isProgram && (
                    <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}>
                        {t('program.signInSubtitle', 'Sign in with the address the library invited.')}
                    </Typography>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box component="form" noValidate autoComplete="off">
                    <TextField
                        fullWidth
                        type="email"
                        slotProps={{ inputLabel: { shrink: true } }}
                        autoComplete="off"
                        label={t('email', 'Email')}
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        slotProps={{
                            inputLabel: { shrink: true },
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(v => !v)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                        autoComplete="off"
                        label={t('password', 'Password')}
                        variant="outlined"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 3 }}
                    />
                    <Button
                        fullWidth
                        variant="outlined"
                        color="primary"
                        disabled={isLoading || password.length === 0 || email.length === 0}
                        onClick={handleSignIn}
                        sx={{
                            fontSize: '1rem',
                            padding: '12px 0',
                            textTransform: 'none'
                        }}
                    >
                        {isLoading && <CircularProgress size={20} color="inherit" sx={{ marginRight: '15px' }} />}
                        {t('signIn', 'Sign In')}
                    </Button>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mt: 2,
                        }}
                    >
                        {/* Programme accounts are created by the library, never self-registered. */}
                        <Typography variant="body2">
                            {isProgram ? (
                                <Link to="/sign-in" style={{ color: 'inherit', textDecoration: 'none' }}>
                                    {t('program.libraryEntrance', 'Library account')}
                                </Link>
                            ) : (
                                <Link to="/sign-up" style={{ color: '#80a748', textDecoration: 'none' }}>
                                    {t('signUp', 'Sign up')}
                                </Link>
                            )}
                        </Typography>

                        <Typography variant="body2">
                            <Link
                                to={isProgram ? '/program/forgot-password' : '/forgot-password'}
                                style={{ color: isProgram ? 'inherit' : '#80a748', textDecoration: 'none' }}
                            >
                                {t('forgotPassword', 'Forgot password?')}
                            </Link>
                        </Typography>
                    </Box>
                    {!isProgram && <Note i18nKey="signInNoteRich" />}
                </Box>
            </Box>
        </Container>
    );
};

export default SignIn;
